import { z } from 'zod';
import { zEnumBackwardsCompatible } from 'libs/end-to-end/src/types/compatibility/compatibility';

// Order matters on this array. This is the order focus areas are displayed on the front end (left hand side
// menu, filters, etc).
export const FocusAreaConstArr = [
  'Hiring',
  'Compensation',
  'Performance',
  'Engagement',
  'Promotion',
  'Leadership',
  'Attrition',
] as const;

/**
 * IMPORTANT: if you add a new focus area that is more than a single word or doesn't match the route's pathname,
 * update the matching logic in MainLayout.tsx
 */
export const { Enum: FocusAreaEnum, Schema: FocusArea } =
  zEnumBackwardsCompatible({
    options: FocusAreaConstArr,
    migrations: { Wages: 'Compensation' },
    name: 'FocusArea',
  });

export type FocusArea = z.infer<typeof FocusArea>;

export const FocusAreas = Object.values(FocusAreaEnum.Values);

export const FocusAreaMode = z
  .enum(['CompletelyHidden', 'Disabled', 'Enabled'])
  .describe('FocusAreaMode');

export type FocusAreaMode = z.infer<typeof FocusAreaMode>;

export const FocusAreaModes = Object.values(FocusAreaMode.Values);

export const OrganizationFocusAreaModes = z.record(
  FocusArea,
  FocusAreaMode.optional(),
);

export type OrganizationFocusAreaModes = z.infer<
  typeof OrganizationFocusAreaModes
>;

export const getCurrentOrganizationFocusAreaMode = (props: {
  focusAreaModes: OrganizationFocusAreaModes | undefined;
  focusArea: FocusArea;
}) => {
  const { focusAreaModes, focusArea } = props;
  return focusAreaModes?.[focusArea] ?? 'Enabled';
};

export const focusAreaModeIsHidden = ({
  focusArea,
  focusAreaModes,
}: {
  focusAreaModes: OrganizationFocusAreaModes | undefined;
  focusArea: FocusArea;
}): boolean => {
  return focusAreaModes?.[focusArea] === FocusAreaMode.Values.CompletelyHidden;
};

export const FocusAreaIds: Record<FocusArea, number> = {
  Hiring: 1,
  Compensation: 2,
  Performance: 3,
  Promotion: 4,
  Attrition: 5,
  Engagement: 6,
  Leadership: 7,
};

export const SectionName = z.enum([
  'group-trends',
  'solutions',
  'summary',
  'summary-v2',
  'insights',
  'insight-details',
]);

export type SectionName = z.infer<typeof SectionName>;
